import { getConfiguration, saveConfiguration } from "../../../utils/configurationStorage/configurationStorage.js"
import { z } from "zod"

/**
 * @typedef {{
 *   itemPerPage?: number | null,
 *   usingCompactMode?: boolean | null,
 *   styleName?: string | null
 * }} OperationTableConfiguration
 */

const VIEW_NAME = "OperationTable"

/**
 * @type {OperationTableConfiguration}
 */
const defaultValue = {
  styleName: null,
  usingCompactMode: false,
  itemPerPage: 10
}

const OperationTableConfigurationSchema = z.object({
  styleName: z.union([
    z.string(),
    z.null(),
  ]),
  usingCompactMode: z.union([
    z.boolean(),
    z.null(),
  ]),
  itemPerPage: z.union([
    z.number(),
    z.null(),
  ]),
})

/**
 * @param {string} userId
 * @param {string} projectId
 * @returns {Promise<OperationTableConfiguration>}
 */
export async function getOperationTableConfiguration(userId, projectId) {
  return getConfiguration(VIEW_NAME, userId, projectId, defaultValue)
}

/**
 * @param {Partial<OperationTableConfiguration>} configuration
 * @param {string} userId
 * @param {string} projectId
 */
export async function saveOperationTableConfiguration(configuration, userId, projectId) {
  const saved = {...defaultValue, ...(await getOperationTableConfiguration(userId, projectId)), ...configuration}
  try {
    OperationTableConfigurationSchema.parse(saved)
    saveConfiguration(VIEW_NAME, userId, projectId, saved)
  } catch (e) {
    console.log(e)
    console.error("Malformed configuration for OperationTable")
  }
}

function applyConfigurationToOperationTableStore(OperationTableStore, configuration) {
  const savedStyleName = configuration.styleName;
  if (savedStyleName) {
    OperationTableStore.StyleName.set(savedStyleName)
  } else {
  }

  if (configuration.usingCompactMode) {
    OperationTableStore.UsingCompactStyle.set(configuration.usingCompactMode)
  }

  if (configuration.itemPerPage) {
    OperationTableStore.ItemsPerPage.set(configuration.itemPerPage)
  }
}

function subscribeOperationTableStoreWithConfiguration(OperationTableStore, saveFunction) {
  const unsubscribeFunctions = []
  unsubscribeFunctions.push(OperationTableStore.StyleName.subscribe(val => {
    saveFunction({styleName: val})
  }))
  unsubscribeFunctions.push(OperationTableStore.ItemsPerPage.subscribe(val => {
    saveFunction({itemPerPage: val})
  }))
  unsubscribeFunctions.push(OperationTableStore.UsingCompactStyle.subscribe(val => {
    saveFunction({usingCompactMode: val})
  }))
  return unsubscribeFunctions
}

export async function connectOperationTableConfigurationToOperationTableStore(OperationTableStore, userId, projectId) {
  applyConfigurationToOperationTableStore(OperationTableStore, await getOperationTableConfiguration(userId, projectId))

  return subscribeOperationTableStoreWithConfiguration(
    OperationTableStore,
    conf => saveOperationTableConfiguration(conf, userId, projectId)
  )
}
